import { useWhoami } from '@cotera/client/app/stores/org';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@cotera/client/v0/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { Logo } from '@cotera/client/v0/components/ui/logo';
import {
  SidebarMenuButton,
  SidebarMenuItem,
} from '@cotera/client/v0/components/ui/sidebar';
import { Building2, LogOut } from 'lucide-react';
import { useState } from 'react';
import { ScrollArea } from '../ui/scroll-area';
import { Icon } from '@cotera/client/app/components';
import { useTabManager } from '../../tab-manager';
import { useAuth } from '@workos-inc/authkit-react';
import { useLogout } from '@cotera/client/app/hooks/use-logout';

const MenuItem = ({
  icon,
  children,
  onClick,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
}) => {
  return (
    <DropdownMenuItem className="text-sm cursor-pointer" onClick={onClick}>
      {icon} {children}
    </DropdownMenuItem>
  );
};

export function UserMenu() {
  const { user: workosUser, switchToOrganization } = useAuth();
  const isWorkosSession = workosUser !== null && workosUser !== undefined;

  const whoami = useWhoami((x) => x.whoami);
  const org = useWhoami((x) => x.org)!;
  const changeOrg = useWhoami((s) => s.actions.changeOrg);
  const [open, setOpen] = useState(false);
  const tabManager = useTabManager();
  const { logout } = useLogout();

  // Get other orgs (works for both WorkOS and legacy sessions since whoami.orgs
  // is now populated from WorkOS for WorkOS users)
  const otherOrgs = whoami.orgs
    .filter((x) => x.id !== org.id)
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleOrgSwitch = async (targetOrg: (typeof whoami.orgs)[number]) => {
    tabManager.closeAllTabs();

    if (isWorkosSession && targetOrg.workosId !== null) {
      // WorkOS session: switch token context first
      await switchToOrganization({ organizationId: targetOrg.workosId });
    }

    // changeOrg handles localStorage, navigation, and reload for both paths
    changeOrg(targetOrg.id);
  };

  return (
    <SidebarMenuItem>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground active:bg-zinc-200 cursor-pointer w-full focus:outline-none focus:ring-0 focus-visible:outline-none"
            data-state={open ? 'open' : 'closed'}
          >
            <Avatar className="h-7 w-7 rounded-md">
              <AvatarImage src="" alt={whoami.name ?? ''} />
              <AvatarFallback className="rounded-lg">
                <Logo />
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{org.name}</span>
              {/* <span className="truncate text-xs">{whoami.email}</span> */}
            </div>
            <Icon icon={'chevron-up-down'} />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-80 rounded-lg z-50"
          side="bottom"
          align="start"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="h-7 w-7 rounded-md">
                <AvatarImage src="" alt={whoami.name ?? ''} />
                <AvatarFallback className="rounded-lg">
                  <Logo />
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{whoami.name}</span>
                <span className="truncate text-xs">{whoami.email}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          {otherOrgs.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Teams
              </DropdownMenuLabel>
              <ScrollArea className="h-[200px]">
                {otherOrgs.map((otherOrg) => (
                  <MenuItem
                    key={otherOrg.id}
                    icon={<Building2 />}
                    onClick={() => handleOrgSwitch(otherOrg)}
                  >
                    {otherOrg.name}
                  </MenuItem>
                ))}
              </ScrollArea>
            </>
          )}
          <DropdownMenuSeparator />
          <MenuItem icon={<LogOut />} onClick={() => logout()}>
            Log out
          </MenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
